import { useEffect, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Define keyboard shortcuts
interface Shortcut {
  key: string;
  meta?: boolean;
  ctrl?: boolean;
  alt?: boolean;
  shift?: boolean;
}

const SHORTCUTS: Record<string, Shortcut> = {
  SEARCH: { key: 'k', meta: true, ctrl: true }, // Cmd+K or Ctrl+K
  NEW_SERVICE: { key: 'n', meta: true, ctrl: true }, // Cmd+N or Ctrl+N
  DASHBOARD: { key: 'd', meta: true, ctrl: true }, // Cmd+D or Ctrl+D
  SERVICES: { key: 's', meta: true, ctrl: true }, // Cmd+S or Ctrl+S
  HELP: { key: 'h', shift: true, meta: true, ctrl: true }, // Cmd+Shift+H
  ESCAPE: { key: 'Escape' }, // ESC key
  TOGGLE_THEME: { key: 't', alt: true, shift: true }, // Alt+Shift+T
};

interface ShortcutCallbacks {
  onSearch?: () => void;
  onNewService?: () => void;
  onEscape?: () => void;
  onToggleTheme?: () => void;
}

export function useKeyboardShortcuts(callbacks?: ShortcutCallbacks) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isSearchOpen, setIsSearchOpen] = useState(false); // Always start with search closed
  const [searchQuery, setSearchQuery] = useState('');

  const handleSearch = useCallback(() => {
    if (callbacks?.onSearch) {
      callbacks.onSearch();
    } else {
      setIsSearchOpen(true);
    }
  }, [callbacks]);

  const handleNewService = useCallback(() => {
    if (callbacks?.onNewService) {
      callbacks.onNewService();
    } else {
      navigate('/dashboard/services/new');
    }
  }, [callbacks, navigate]);

  const handleDashboard = useCallback(() => {
    navigate('/dashboard');
  }, [navigate]);

  const handleServices = useCallback(() => {
    navigate('/dashboard/services');
  }, [navigate]);

  const handleHelp = useCallback(() => {
    // Show help modal or navigate to help page
    console.log('Help shortcut triggered');
    // You can implement a help modal here
  }, []);

  const handleEscape = useCallback(() => {
    if (callbacks?.onEscape) {
      callbacks.onEscape();
    } else {
      setIsSearchOpen(false);
      setSearchQuery('');
    }
  }, [callbacks]);

  const handleToggleTheme = useCallback(() => {
    if (callbacks?.onToggleTheme) {
      callbacks.onToggleTheme();
    } else {
      // Toggle theme
      const currentTheme = document.documentElement.classList.contains('dark') ? 'dark' : 'light';
      const newTheme = currentTheme === 'dark' ? 'light' : 'dark';
      document.documentElement.classList.toggle('dark');
      localStorage.setItem('theme', newTheme);
    }
  }, [callbacks]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Don't trigger shortcuts when typing in input fields
      if (
        event.target instanceof HTMLInputElement ||
        event.target instanceof HTMLTextAreaElement ||
        event.target instanceof HTMLSelectElement
      ) {
        // Allow Escape key even in input fields
        if (event.key === 'Escape') {
          handleEscape();
        }
        return;
      }

      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const metaKey = isMac ? event.metaKey : event.ctrlKey;

      // Check for search shortcut (Cmd/Ctrl + K)
      if (metaKey && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        handleSearch();
        return;
      }

      // Check for new service shortcut (Cmd/Ctrl + N)
      if (metaKey && event.key.toLowerCase() === 'n') {
        event.preventDefault();
        handleNewService();
        return;
      }

      // Check for dashboard shortcut (Cmd/Ctrl + D)
      if (metaKey && event.key.toLowerCase() === 'd') {
        event.preventDefault();
        handleDashboard();
        return;
      }

      // Check for services shortcut (Cmd/Ctrl + S)
      if (metaKey && event.key.toLowerCase() === 's') {
        event.preventDefault();
        handleServices();
        return;
      }

      // Check for help shortcut (Cmd/Ctrl + Shift + H)
      if (metaKey && event.shiftKey && event.key.toLowerCase() === 'h') {
        event.preventDefault();
        handleHelp();
        return;
      }

      // Check for theme toggle (Alt + Shift + T)
      if (event.altKey && event.shiftKey && event.key.toLowerCase() === 't') {
        event.preventDefault();
        handleToggleTheme();
        return;
      }

      // Escape key (works everywhere)
      if (event.key === 'Escape') {
        handleEscape();
        return;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    handleSearch,
    handleNewService,
    handleDashboard,
    handleServices,
    handleHelp,
    handleEscape,
    handleToggleTheme,
  ]);

  return {
    isSearchOpen,
    setIsSearchOpen,
    searchQuery,
    setSearchQuery,
    shortcuts: SHORTCUTS,
  };
}

// Component to display shortcut hints
export function ShortcutHint({ shortcut, label }: { shortcut: keyof typeof SHORTCUTS; label: string }) {
  const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
  const shortcutDef = SHORTCUTS[shortcut];
  
  const getShortcutText = () => {
    let keys = [];
    
    if (shortcutDef?.meta || shortcutDef?.ctrl) {
      keys.push(isMac ? '⌘' : 'Ctrl');
    }
    if (shortcutDef?.alt) {
      keys.push(isMac ? '⌥' : 'Alt');
    }
    if (shortcutDef?.shift) {
      keys.push('⇧');
    }
    
    if (shortcutDef.key === 'Escape') {
      keys.push('ESC');
    } else {
      keys.push(shortcutDef.key.toUpperCase());
    }
    
    return keys.join(isMac ? '' : '+');
  };

  return (
    <span className="inline-flex items-center gap-2 text-sm text-muted-foreground">
      <span>{label}</span>
      <kbd className="px-2 py-1 text-xs font-semibold text-foreground bg-muted border border-border rounded">
        {getShortcutText()}
      </kbd>
    </span>
  );
}

// Help dialog component to show all shortcuts
export function KeyboardShortcutsHelp() {
  const { t } = useTranslation();
  const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;

  const shortcuts = [
    { key: 'SEARCH', label: t('shortcuts.search', 'Quick Search') },
    { key: 'NEW_SERVICE', label: t('shortcuts.newService', 'New Service') },
    { key: 'DASHBOARD', label: t('shortcuts.dashboard', 'Go to Dashboard') },
    { key: 'SERVICES', label: t('shortcuts.services', 'Go to Services') },
    { key: 'TOGGLE_THEME', label: t('shortcuts.toggleTheme', 'Toggle Theme') },
    { key: 'HELP', label: t('shortcuts.help', 'Show Help') },
    { key: 'ESCAPE', label: t('shortcuts.escape', 'Close/Cancel') },
  ];

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold">{t('shortcuts.title', 'Keyboard Shortcuts')}</h3>
      <div className="space-y-2">
        {shortcuts.map(({ key, label }) => (
          <div key={key} className="flex items-center justify-between py-2">
            <span className="text-sm">{label}</span>
            <ShortcutHint shortcut={key as keyof typeof SHORTCUTS} label="" />
          </div>
        ))}
      </div>
      <p className="text-xs text-muted-foreground mt-4">
        {isMac
          ? t('shortcuts.hint.mac', 'Use ⌘ for Command key shortcuts')
          : t('shortcuts.hint.pc', 'Use Ctrl for keyboard shortcuts')}
      </p>
    </div>
  );
}